import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ThemeToggle from "../components/ThemeToggle";
import { Loader2, ArrowRight } from "lucide-react";

const BG_LIGHT =
  "https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BG_DARK =
  "https://images.unsplash.com/photo-1633624875862-2a3c50468718?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, loading, login } = useAuth();
  const { theme } = useTheme();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) nav("/", { replace: true });
    else setError(res.error || "Sign in failed");
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background">
      {/* Left: form */}
      <div className="flex flex-col px-6 py-8 md:px-12 lg:px-16">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="login-brand">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-display font-bold">
              M
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-bold tracking-tight text-base">MyID BI</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Subscription & customer intelligence
              </span>
            </div>
          </div>
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center">
          <div className="w-full max-w-sm mx-auto lg:mx-0">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Sign in to your <span className="text-primary">dashboard</span>
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              One view across Web, iOS, Android, and Shopify — MyID+ growth, revenue, and true customer LTV.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@myid.com"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div
                  className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
                  data-testid="login-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full group"
                size="lg"
                data-testid="login-submit"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Sign in <ArrowRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center pt-2">
                Need access? Ask your admin to add you in Settings → Team.
              </p>
            </form>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} MyID BI</p>
      </div>

      {/* Right: visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={theme === "dark" ? BG_DARK : BG_LIGHT}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-background/30" />
        <div className="absolute bottom-10 left-10 right-10 max-w-md">
          <div className="rounded-2xl border border-border bg-card/80 backdrop-blur-xl p-6 shadow-sm">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium">
              MyID+ & Commerce Insights
            </p>
            <p className="mt-3 font-display text-xl font-semibold leading-snug">
              Subscription analytics fused with Shopify customer LTV. No more spreadsheets.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              {["Web", "iOS", "Android"].map((p) => (
                <div key={p} className="rounded-lg border border-border px-2 py-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {p}
                  </p>
                  <p className="font-display font-semibold mt-0.5">Live</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
